/**
 * Admin-inbox widgets (#1805 slice A3b) — the seven `fields[].render` callbacks
 * the review-queue and webhook-failures inboxes carried, moved to their only v2
 * carrier, plus the presentation helpers those callbacks used.
 *
 * Every one of them reads the RECORD rather than the cell value, which is
 * exactly why they were callbacks: the review kind badge discriminates on
 * `cause` before it looks at `kind`, the summary cell rebuilds a localized
 * sentence from `details_json` and appends the trace id, and the failure
 * message cell pairs `error_message_head` with `trace_id`. Each therefore
 * carries `csvText: stringifyRaw(value)` — the text the bespoke-renderer branch
 * of `formatFieldTextForExport` already produced — so the export does not move.
 * That is load-bearing on two columns in particular: review `kind` exports the
 * RAW wire token rather than its enum label, and failure `received_at` exports
 * the raw ISO string while its same-typed neighbour `created_at` exports the
 * merchant-timezone rendering.
 *
 * They translate through the i18n singleton rather than `useTranslation`
 * because the callbacks they replace did: both resolve against the same live
 * instance, but only the literal copy guarantees byte-identical markup, which
 * is what the two `__tests__/*.v2-parity.test.tsx` LOCKs compare.
 *
 * NO `@services/*` import, and that is a hard constraint rather than a style
 * choice: `src/features/widgets.ts` is evaluated by `src/test-setup.ts` for
 * EVERY suite, ahead of any `vi.mock`, so a service reached from a widget
 * module is instantiated against the real `@lib/api` before a test can replace
 * it (that cost 19 red tests in slice A1). The closed sets these projections
 * need therefore live in `@/services/*.constants`, which hold no transport;
 * `webhookFailureBadges` reaches the same leaf.
 *
 * The detail-JSON readers are exported because `./pages/reviewQueueResource.tsx`
 * needs them for its payload tab, and the dependency may only point THIS way —
 * that module imports `reviewQueueAPI`, so a widget reaching back into it would
 * reintroduce exactly the edge the paragraph above forbids.
 */
import { Badge, stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import i18n from '@/i18n';
import { closedEnumLookup } from '@lib/closedEnumGuard';
import { formatDateTime } from '@lib/datetime';
import type { TFunction } from 'i18next';
import {
  POST_DISPATCH_CANCELLATION_CAUSE,
  REVIEW_ERROR_KIND,
  REVIEW_QUEUE_KIND,
  type PostDispatchCancellationCause,
  type ReviewErrorKind,
  type ReviewQueueKind,
} from '@/services/review-queue.constants';
import type { components } from '@/types/api.generated';
import {
  badgeVariantFor,
  providerBadgeVariantFor,
  selfHealBadgeVariantFor,
  selfHealLabelKeyFor,
} from './components/webhookFailureBadges';

type ReviewQueueItem = components['schemas']['ReviewQueueItem'];
type WebhookFailureListItem = components['schemas']['WebhookFailureListItem'];

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    reviewKindBadge: { types: 'enum'; modes: 'list' };
    reviewSummary: { types: 'text'; modes: 'list' };
    webhookFailureProvider: { types: 'enum'; modes: 'list' };
    webhookFailureTopic: { types: 'text'; modes: 'list' };
    webhookFailureErrorKind: { types: 'enum'; modes: 'list' };
    webhookFailureErrorMessage: { types: 'text'; modes: 'list' };
    webhookFailureReceivedAt: { types: 'datetime'; modes: 'list' };
  }
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

// ─── Review queue: badge + summary presentation (ported verbatim) ────────────

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const KIND_BADGE_VARIANT: Record<ReviewQueueKind, BadgeVariant> = {
  [REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE]: 'destructive',
  [REVIEW_QUEUE_KIND.POST_DISPATCH_CANCELLATION]: 'destructive',
  [REVIEW_QUEUE_KIND.DISPATCH_FAILED]: 'destructive',
};
const kindBadgeVariantFor = closedEnumLookup<ReviewQueueKind, BadgeVariant>(
  KIND_BADGE_VARIANT,
  'outline',
);

// Cause → short badge label (discriminates the wide `post_dispatch_cancellation`
// bucket). Derived from the closed set so a cause is never a scattered literal.
const CAUSE_BADGE_I18N: Record<PostDispatchCancellationCause, string> = {
  [POST_DISPATCH_CANCELLATION_CAUSE.SHOPIFY_CANCELLATION_AFTER_DISPATCH]:
    'reviewQueue.cause.shopify_cancellation_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.ORDER_REFUNDED_AFTER_DISPATCH]:
    'reviewQueue.cause.order_refunded_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.QUANTITY_CHANGED_AFTER_DISPATCH]:
    'reviewQueue.cause.quantity_changed_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.ADDRESS_CHANGED_AFTER_DISPATCH]:
    'reviewQueue.cause.address_changed_after_dispatch',
};
const CAUSE_SUMMARY_I18N: Record<PostDispatchCancellationCause, string> = {
  [POST_DISPATCH_CANCELLATION_CAUSE.SHOPIFY_CANCELLATION_AFTER_DISPATCH]:
    'reviewQueue.summaryCause.shopify_cancellation_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.ORDER_REFUNDED_AFTER_DISPATCH]:
    'reviewQueue.cause.order_refunded_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.QUANTITY_CHANGED_AFTER_DISPATCH]:
    'reviewQueue.summaryCause.quantity_changed_after_dispatch',
  [POST_DISPATCH_CANCELLATION_CAUSE.ADDRESS_CHANGED_AFTER_DISPATCH]:
    'reviewQueue.summaryCause.address_changed_after_dispatch',
};
const ERROR_KIND_I18N: Record<ReviewErrorKind, string> = {
  [REVIEW_ERROR_KIND.PROVIDER_REJECTED]: 'reviewQueue.errorKind.provider_rejected',
  [REVIEW_ERROR_KIND.PROVIDER_UNREACHABLE]: 'reviewQueue.errorKind.provider_unreachable',
  [REVIEW_ERROR_KIND.UNEXPECTED]: 'reviewQueue.errorKind.unexpected',
};

const causeBadgeKeyFor = closedEnumLookup<PostDispatchCancellationCause, string | undefined>(
  CAUSE_BADGE_I18N,
  undefined,
);
const causeSummaryKeyFor = closedEnumLookup<PostDispatchCancellationCause, string | undefined>(
  CAUSE_SUMMARY_I18N,
  undefined,
);
const errorKindKeyFor = closedEnumLookup<ReviewErrorKind, string | undefined>(
  ERROR_KIND_I18N,
  undefined,
);

/**
 * The row behind a review cell.
 *
 * A widening cast rather than a `failHard`, matching the callbacks these
 * replace: they read `item.cause` / `item.details_json` off whatever they were
 * handed and degraded rather than threw, so a partial row still renders the
 * markup it always did.
 */
function reviewItemOf(record: unknown): ReviewQueueItem {
  return (typeof record === 'object' && record !== null ? record : {}) as ReviewQueueItem;
}

/** The operator-facing type label: the cause label when the row carries a known
 *  `cause` (discriminating the wide bucket), else the kind label. An out-of-set
 *  cause is wire drift — warn and fall back to the kind label. */
export function typeBadgeLabel(item: ReviewQueueItem, t: TFunction): string {
  if (item.cause) {
    const badgeKey = causeBadgeKeyFor(item.cause);
    if (badgeKey) return t(badgeKey);
    console.warn(
      `[review-queue] Unknown cause '${item.cause}' for kind '${item.kind}' — the cause ` +
        `closed set (services/review-queue.constants.ts) needs an entry, or the wire payload is malformed. ` +
        `Falling back to the kind label.`,
    );
  }
  return t(`reviewQueue.kind.${item.kind}`, { defaultValue: item.kind });
}

/**
 * The row's structured detail, or null when it is absent / unparseable.
 *
 * Exported for `./pages/reviewQueueResource.tsx`'s payload tab — see the file
 * header for why the dependency may only point that way.
 */
export function parseDetails(detailsJson: string | null): Record<string, unknown> | null {
  if (!detailsJson) return null;
  try {
    const parsed: unknown = JSON.parse(detailsJson);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch (err) {
    console.warn(
      '[review-queue] details_json is not parseable JSON — falling back to the backend summary string.',
      err,
    );
    return null;
  }
}

/** A non-empty string member of a parsed detail bag, else undefined. */
export function detailString(rec: Record<string, unknown>, key: string): string | undefined {
  const value = rec[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** The non-empty string members of an array-valued detail member. */
export function detailStringArray(rec: Record<string, unknown>, key: string): string[] {
  const value = rec[key];
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === 'string' && x.length > 0);
}

/** Localized one-line summary rebuilt from the row's structured detail, degrading
 *  to the backend `summary` when it cannot be restructured. */
export function localizedSummary(item: ReviewQueueItem, t: TFunction): string {
  const detail = parseDetails(item.details_json);
  if (!detail) return item.summary;
  // Prefer the joined order name (e.g. `#1001`); fall back to the raw
  // Shopify order id when the order is missing / soft-deleted (#1465).
  const orderId = item.order_name ?? detailString(detail, 'shopify_order_id');
  if (!orderId) return item.summary;

  switch (item.kind) {
    case REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE:
      return t('reviewQueue.summary.post_cutoff_order_change', { orderId });
    case REVIEW_QUEUE_KIND.POST_DISPATCH_CANCELLATION: {
      const cause = item.cause ?? detailString(detail, 'cause');
      const summaryKey = cause ? causeSummaryKeyFor(cause) : undefined;
      if (summaryKey) {
        return t('reviewQueue.summary.post_dispatch_cancellation', {
          orderId,
          cause: t(summaryKey),
        });
      }
      return item.summary;
    }
    case REVIEW_QUEUE_KIND.DISPATCH_FAILED: {
      const errorKind = detailString(detail, 'error_kind') ?? '';
      const errorKindKey = errorKindKeyFor(errorKind);
      return t('reviewQueue.summary.dispatch_failed', {
        orderId,
        errorKind: errorKindKey ? t(errorKindKey) : errorKind,
      });
    }
    default:
      return item.summary;
  }
}

function ReviewKindBadge({ record }: WidgetProps) {
  const row = reviewItemOf(record);
  return (
    <Badge variant={kindBadgeVariantFor(row.kind)} className="text-[10px] uppercase">
      {typeBadgeLabel(row, i18n.t)}
    </Badge>
  );
}

function ReviewSummary({ record }: WidgetProps) {
  const row = reviewItemOf(record);
  return (
    <div>
      <div className="max-w-md break-words">{localizedSummary(row, i18n.t)}</div>
      <div className="mt-1 text-[10px] text-muted-foreground">
        {i18n.t('reviewQueue.row.traceLabel', { traceId: row.trace_id })}
      </div>
    </div>
  );
}

// ─── Webhook failures ────────────────────────────────────────────────────────

/** Same widening-cast argument as `reviewItemOf`. */
function failureOf(record: unknown): WebhookFailureListItem {
  return (typeof record === 'object' && record !== null ? record : {}) as WebhookFailureListItem;
}

function WebhookFailureProvider({ record }: WidgetProps) {
  const row = failureOf(record);
  return (
    <Badge variant={providerBadgeVariantFor(row.provider)} className="text-[10px] uppercase">
      {i18n.t(`webhookFailures.provider.${row.provider}`, { defaultValue: row.provider })}
    </Badge>
  );
}

function WebhookFailureTopic({ record }: WidgetProps) {
  return <span className="font-mono text-xs">{failureOf(record).topic}</span>;
}

/**
 * The error kind, plus — on the LIST surface only — the backend's self-heal
 * verdict for the row (#2017).
 *
 * The verdict rides this cell rather than a column of its own because it is a
 * COMPUTED view, not a stored one: it is neither filterable nor sortable
 * server-side, and the model's field set is frozen by two LOCKs that enumerate
 * every column (`resource-field-sets.snapshot.json`, the v2-parity golden). It
 * belongs beside the error kind anyway — the verdict only ever exists for
 * `d1_write_failed`, so the two badges answer one question together: this write
 * failed, and did a later delivery already fix it?
 *
 * DETAIL deliberately gets nothing here. The drawer renders this cell and the
 * diagnosis tab on ONE page, and the tab states the verdict with the criterion
 * that proved it and a link to the event that succeeded. A badge repeating the
 * verdict three inches above it is noise, not redundancy.
 *
 * `self_heal: null` — a resolved row, or any other error kind — renders exactly
 * the markup this cell rendered before the verdict existed. "Not applicable" is
 * not "unknown", and the panel must not manufacture an answer the backend
 * declined to give.
 */
function WebhookFailureErrorKind({ record, surface }: WidgetProps) {
  const row = failureOf(record);
  const kindBadge = (
    <Badge variant={badgeVariantFor(row.error_kind)} className="text-[10px] uppercase">
      {i18n.t(`webhookFailures.errorKind.${row.error_kind}`, { defaultValue: row.error_kind })}
    </Badge>
  );
  const selfHeal = surface === 'detail' ? null : row.self_heal;
  // An out-of-set verdict is wire drift: show the error kind alone rather
  // than a badge carrying a raw token no operator can read.
  const verdictKey = selfHeal ? selfHealLabelKeyFor(selfHeal.verdict) : undefined;
  if (!selfHeal || !verdictKey) return kindBadge;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {kindBadge}
      <Badge variant={selfHealBadgeVariantFor(selfHeal.verdict)} className="text-[10px] uppercase">
        {i18n.t(verdictKey)}
      </Badge>
    </div>
  );
}

/**
 * The two surfaces want opposite things from the same string, so this cell
 * dispatches on `surface` rather than picking one:
 *
 *   LIST   — one line, clipped. The table's job is scanning, and a message that
 *            wrapped would give every row a different height.
 *   DETAIL — the whole message, wrapped. The drawer is where an operator has
 *            come to READ the error; a one-line ellipsis with the rest hidden
 *            in a `title` tooltip is the single most useless thing this panel
 *            could show. It is already capped at 200 characters by the backend
 *            (ADR-0004), so "in full" is a bounded promise.
 *
 * `break-words` is not the absence of clipping but a request: without it a long
 * unbroken token (a URL, a base64 blob) overflows the column instead of
 * wrapping inside it. The trace id rides along on both surfaces because it is
 * what an operator pastes into the log search.
 */
function WebhookFailureErrorMessage({ record, surface }: WidgetProps) {
  const row = failureOf(record);
  return (
    <div>
      <div
        className={
          surface === 'detail'
            ? 'whitespace-pre-wrap break-words text-xs'
            : 'max-w-[360px] truncate text-xs'
        }
        title={row.error_message_head}
      >
        {row.error_message_head}
      </div>
      <div className="mt-1 text-[10px] text-muted-foreground">
        {i18n.t('webhookFailures.row.traceLabel', { traceId: row.trace_id })}
      </div>
    </div>
  );
}

function WebhookFailureReceivedAt({ record }: WidgetProps) {
  return (
    <span className="text-xs text-muted-foreground">
      {formatDateTime(failureOf(record).received_at)}
    </span>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerAdminWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('reviewKindBadge', {
    display: ReviewKindBadge,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  registerWidget('reviewSummary', {
    display: ReviewSummary,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('webhookFailureProvider', {
    display: WebhookFailureProvider,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  registerWidget('webhookFailureTopic', {
    display: WebhookFailureTopic,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('webhookFailureErrorKind', {
    display: WebhookFailureErrorKind,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  registerWidget('webhookFailureErrorMessage', {
    display: WebhookFailureErrorMessage,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('webhookFailureReceivedAt', {
    display: WebhookFailureReceivedAt,
    csvText: rawCsv,
    supportedTypes: ['datetime'],
    modes: ['list'],
  });
}
